import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Card, 
  Table, 
  Button, 
  Space, 
  Tag, 
  Modal, 
  Form, 
  Input, 
  Switch, 
  InputNumber, 
  Select,
  message,
  Popconfirm,
  Drawer,
  Typography,
  Divider,
} from 'antd'
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  ReloadOutlined,
  CopyOutlined,
} from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import type { ColumnsType } from 'antd/es/table'
import { api } from '@services/api'
import type { TokenInfo, TokenCreateData, TokenMutexRule } from '../types/api'

const { Option } = Select
const { Text, Paragraph } = Typography

const Tokens: React.FC = () => {
  const navigate = useNavigate()
  const [createModalVisible, setCreateModalVisible] = useState(false)
  const [editingToken, setEditingToken] = useState<TokenInfo | null>(null)
  const [detailDrawerVisible, setDetailDrawerVisible] = useState(false)
  const [selectedToken, setSelectedToken] = useState<TokenInfo | null>(null)
  const [mutexModalVisible, setMutexModalVisible] = useState(false)
  const [editingMutexRule, setEditingMutexRule] = useState<TokenMutexRule | null>(null)
  const [form] = Form.useForm()
  const [mutexForm] = Form.useForm()
  const queryClient = useQueryClient()

  const openEditModal = async (tokenId: number) => {
    try {
      const token = await api.token.get(tokenId)
      setEditingToken(token)
      form.setFieldsValue({
        ...token,
        group_item_limits_json: token.group_item_limits
          ? JSON.stringify(token.group_item_limits, null, 2)
          : '{}',
      })
    } catch (error) {
      message.error('获取Token详情失败')
    }
  }

  // 复制Token到剪贴板
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text)
      message.success('Token已复制到剪贴板')
    } catch (error) {
      // 降级方案
      const textArea = document.createElement('textarea')
      textArea.value = text
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      message.success('Token已复制到剪贴板')
    }
  }

  // 获取Token列表
  const { data: tokens, isLoading, refetch } = useQuery({
    queryKey: ['tokens'],
    queryFn: () => api.token.list(),
  })

  const { data: mutexRules, isLoading: mutexLoading } = useQuery({
    queryKey: ['token_mutex_rules'],
    queryFn: () => api.token.listMutexRules(),
  })

  const { data: selectedExchangeStatus, isLoading: exchangeStatusLoading } = useQuery({
    queryKey: ['token-exchange-status', selectedToken?.id],
    queryFn: () => api.token.getExchangeStatus(Number(selectedToken?.id)),
    enabled: !!selectedToken?.id,
    refetchInterval: 30000,
  })

  // 创建Token
  const createMutation = useMutation({
    mutationFn: api.token.create,
    onSuccess: () => {
      message.success('Token创建成功')
      setCreateModalVisible(false)
      form.resetFields()
      queryClient.invalidateQueries({ queryKey: ['tokens'] })
    },
  })

  // 更新Token
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.token.update(id, data),
    onSuccess: () => {
      message.success('Token更新成功')
      setEditingToken(null)
      form.resetFields()
      queryClient.invalidateQueries({ queryKey: ['tokens'] })
    },
  })

  // 删除Token
  const deleteMutation = useMutation({
    mutationFn: api.token.delete,
    onSuccess: () => {
      message.success('Token删除成功')
      queryClient.invalidateQueries({ queryKey: ['tokens'] })
    },
  })

  const createMutexMutation = useMutation({
    mutationFn: api.token.createMutexRule,
    onSuccess: () => {
      message.success('互斥规则创建成功')
      setMutexModalVisible(false)
      setEditingMutexRule(null)
      mutexForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['token_mutex_rules'] })
    },
  })

  const updateMutexMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => api.token.updateMutexRule(id, data),
    onSuccess: () => {
      message.success('互斥规则更新成功')
      setMutexModalVisible(false)
      setEditingMutexRule(null)
      mutexForm.resetFields()
      queryClient.invalidateQueries({ queryKey: ['token_mutex_rules'] })
    },
  })

  const deleteMutexMutation = useMutation({
    mutationFn: (id: number) => api.token.deleteMutexRule(id),
    onSuccess: () => {
      message.success('互斥规则删除成功')
      queryClient.invalidateQueries({ queryKey: ['token_mutex_rules'] })
    },
  })

  // 表格列定义
  const columns: ColumnsType<TokenInfo> = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: 'Token名称',
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
    {
      title: 'Token值',
      dataIndex: 'token',
      key: 'token',
      ellipsis: true,
      render: (token) => (
        <code style={{ fontSize: '12px' }}>
          {token.substring(0, 20)}...
        </code>
      ),
    },
    {
      title: '状态',
      dataIndex: 'is_active',
      key: 'is_active',
      width: 100,
      render: (isActive) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? '激活' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '交易所',
      dataIndex: 'exchange_name',
      key: 'exchange_name',
      width: 140,
      render: (exchange, record) => (
        <>
          <Tag color="blue" style={{ textTransform: 'uppercase' }}>
            {exchange}
          </Tag>
          {record?.tradfi_enabled && (
            <Tag color="orange">CFD</Tag>
          )}
        </>
      ),
    },
    {
      title: '实例状态',
      dataIndex: 'exchange_status',
      key: 'exchange_status',
      width: 120,
      render: (status) => {
        const connected = !!status?.connected
        return (
          <Tag color={connected ? 'green' : 'red'}>
            {connected ? '正常' : '异常'}
          </Tag>
        )
      },
    },
    {
      title: '使用次数',
      dataIndex: 'usage_count',
      key: 'usage_count',
      width: 100,
      align: 'right',
    },
    {
      title: '交易权限',
      dataIndex: 'allow_trading',
      key: 'allow_trading',
      width: 100,
      render: (allowed) => (
        <Tag color={allowed ? 'green' : 'orange'}>
          {allowed ? '允许' : '禁止'}
        </Tag>
      ),
    },
    {
      title: '执行模式',
      dataIndex: 'execution_mode',
      key: 'execution_mode',
      width: 130,
      render: (mode: string, record: any) => {
        const m = String(
          mode || (record?.maker_market_enabled ? 'maker_market' : 'off')
        ).toLowerCase()
        const map: Record<string, { color: string; text: string }> = {
          off: { color: 'default', text: '普通市价' },
          maker_market: { color: 'green', text: '市价转限价' },
          limit_offset: { color: 'blue', text: '偏移百分比限价' },
        }
        const info = map[m] || map.off
        return <Tag color={info.color}>{info.text}</Tag>
      },
    },
    {
      title: '仓位倍数',
      dataIndex: 'position_multiplier',
      key: 'position_multiplier',
      width: 90,
      align: 'right' as const,
      render: (v: number | undefined) => v ?? 1,
    },
    {
      title: '最后使用',
      dataIndex: 'last_used_at',
      key: 'last_used_at',
      width: 180,
      render: (date) => date ? new Date(date).toLocaleString() : '-',
    },
    {
      title: '操作',
      key: 'actions',
      width: 240,
      render: (_, record) => (
        <Space size="small">
          <Button
            size="small"
            onClick={() => navigate(`/tokens/${record.id}/orders`)}
          >
            订单管理
          </Button>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => {
              setSelectedToken(record)
              setDetailDrawerVisible(true)
            }}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => openEditModal(record.id)}
          />
          <Popconfirm
            title="确定删除这个Token吗？"
            onConfirm={() => deleteMutation.mutate(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  const mutexColumns: ColumnsType<TokenMutexRule> = [
    {
      title: 'Token A',
      key: 'token_a',
      render: (_, record) => `${record.token_a_name || '-'} (#${record.token_a_id})`,
    },
    {
      title: 'Token B',
      key: 'token_b',
      render: (_, record) => `${record.token_b_name || '-'} (#${record.token_b_id})`,
    },
    {
      title: '互斥币种',
      dataIndex: 'symbols',
      key: 'symbols',
      render: (symbols: string[]) => (
        <Space wrap>
          {(symbols || []).map((s) => (
            <Tag key={s} color="geekblue">{s}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 100,
      render: (enabled: boolean) => (
        <Tag color={enabled ? 'green' : 'default'}>{enabled ? '启用' : '禁用'}</Tag>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => {
              setEditingMutexRule(record)
              setMutexModalVisible(true)
              mutexForm.setFieldsValue({
                token_a_id: record.token_a_id,
                token_b_id: record.token_b_id,
                symbols: (record.symbols || []).join(','),
                enabled: record.enabled,
              })
            }}
          />
          <Popconfirm
            title="确定删除这条互斥规则吗？"
            onConfirm={() => deleteMutexMutation.mutate(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  // 处理表单提交
  const parseGroupItemLimits = (raw: unknown): Record<string, number> | undefined => {
    if (raw === undefined || raw === null || raw === '') {
      return undefined
    }
    let obj: Record<string, unknown>
    if (typeof raw === 'string') {
      try {
        obj = JSON.parse(raw)
      } catch {
        throw new Error('Group 并发限制必须是合法 JSON，例如 {"1":1,"2":2}')
      }
    } else if (typeof raw === 'object') {
      obj = raw as Record<string, unknown>
    } else {
      throw new Error('Group 并发限制格式无效')
    }
    const out: Record<string, number> = {}
    for (const [k, v] of Object.entries(obj)) {
      const n = Number(v)
      if (!k.trim() || !Number.isFinite(n) || n < 1) {
        throw new Error(`Group "${k}" 的上限必须是大于 0 的整数`)
      }
      out[String(k).trim()] = Math.floor(n)
    }
    return out
  }

  const handleSubmit = async (values: any) => {
    try {
      const { group_item_limits_json, ...rest } = values
      const data: TokenCreateData = {
        ...rest,
      }
      if (group_item_limits_json !== undefined) {
        data.group_item_limits = parseGroupItemLimits(group_item_limits_json)
      }

      if (editingToken) {
        await updateMutation.mutateAsync({ id: editingToken.id, data })
      } else {
        await createMutation.mutateAsync(data)
      }
    } catch (error: any) {
      message.error(error?.message || '保存失败')
      console.error('Submit error:', error)
    }
  }

  const handleMutexSubmit = async (values: any) => {
    const symbols = String(values.symbols || '')
      .split(',')
      .map((s) => s.trim().toUpperCase())
      .filter(Boolean)
    const payload = {
      token_a_id: Number(values.token_a_id),
      token_b_id: Number(values.token_b_id),
      symbols,
      enabled: !!values.enabled,
    }
    if (editingMutexRule) {
      await updateMutexMutation.mutateAsync({ id: editingMutexRule.id, data: payload })
    } else {
      await createMutexMutation.mutateAsync(payload)
    }
  }

  return (
    <div className="page-inner">
      <Card
        title="Token管理"
        extra={
          <Space>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              刷新
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => setCreateModalVisible(true)}
            >
              创建Token
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={tokens}
          loading={isLoading}
          rowKey="id"
          pagination={{
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
        />
      </Card>

      <Card
        title="Token互斥规则"
        style={{ marginTop: 16 }}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => {
              setEditingMutexRule(null)
              mutexForm.resetFields()
              mutexForm.setFieldsValue({ enabled: true })
              setMutexModalVisible(true)
            }}
          >
            新增规则
          </Button>
        }
      >
        <Table
          columns={mutexColumns}
          dataSource={mutexRules || []}
          loading={mutexLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>

      {/* 创建/编辑Modal */}
      <Modal
        title={editingToken ? '编辑Token' : '创建Token'}
        open={createModalVisible || !!editingToken}
        onCancel={() => {
          setCreateModalVisible(false)
          setEditingToken(null)
          form.resetFields()
        }}
        onOk={() => form.submit()}
        confirmLoading={createMutation.isPending || updateMutation.isPending}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            allow_trading: true,
            allow_risk_check: true,
            allow_short: false,
            max_daily_signals: 1000,
            max_position_size_usdt: 10000,
            position_multiplier: 1,
            exchange_name: 'binance',
            test_mode: false,
            execution_mode: 'off',
            notify_email: undefined,
          }}
        >
          <Form.Item
            name="name"
            label="Token名称"
            rules={[{ required: true, message: '请输入Token名称' }]}
          >
            <Input placeholder="请输入Token名称" />
          </Form.Item>

          <Form.Item
            name="description"
            label="描述"
          >
            <Input.TextArea placeholder="请输入Token描述" rows={3} />
          </Form.Item>

          <Form.Item
            name="exchange_name"
            label="绑定交易所"
            rules={[{ required: true, message: '请选择交易所' }]}
          >
            <Select placeholder="请选择交易所">
              <Option value="binance">Binance</Option>
              <Option value="okx">OKX</Option>
              <Option value="bybit">Bybit</Option>
              <Option value="gate">Gate</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="tradfi_enabled"
            label="Gate TradFi CFD 模式"
            valuePropName="checked"
            extra="启用后使用 Gate /tradfi/ API 交易外汇/商品/指数/股票 CFD"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="tradfi_breakeven_enabled"
            label="CFD 保本止损"
            valuePropName="checked"
            extra="首档止盈触发后，自动将止损价移至开仓价（保本）"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="tradfi_risk_sizing_enabled"
            label="CFD 以损定仓"
            valuePropName="checked"
            extra="忽略webhook仓位，按 可用余额×系数/止损距离 计算手数"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="tradfi_risk_percent"
            label="以损定仓系数 (%)"
            extra="账户可用余额的止损百分比，如 3 表示每笔最多亏 3%"
          >
            <InputNumber
              min={0.01}
              max={100}
              step={0.1}
              style={{ width: '100%' }}
              placeholder="3"
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item
            name="tradfi_pad_min_lots"
            label="手数不足补足 0.02"
            valuePropName="checked"
            extra="以损定仓算出不满 0.02 手时自动补足为 0.02 手（两笔 0.01），启动官方拆单分批止盈"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="tradfi_gold_market_convert_enabled"
            label="黄金限价转市价"
            valuePropName="checked"
            extra="XAUUSD 限价单：限价与新浪市价差在阈值内时自动转为市价单（避免限价不成交）"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="tradfi_gold_market_convert_pct"
            label="转市价阈值 (%)"
            extra="限价与市价的差值百分比，默认 0.05（万分之五）"
          >
            <InputNumber
              min={0.001}
              max={10}
              step={0.01}
              style={{ width: '100%' }}
              placeholder="0.05"
              addonAfter="%"
            />
          </Form.Item>

          <Form.Item name="api_key" label="正式 API Key">
            <Input placeholder="不填表示沿用已有值" />
          </Form.Item>
          <Form.Item name="api_secret" label="正式 API Secret">
            <Input.Password placeholder="不填表示沿用已有值" />
          </Form.Item>
          <Form.Item name="passphrase" label="正式 Passphrase (OKX)">
            <Input.Password placeholder="可选" />
          </Form.Item>
          <Form.Item name="test_mode" label="启用测试模式" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item
            name="execution_mode"
            label="执行模式（market信号）"
            extra="off=普通市价单；maker_market=市价转限价（仅Binance，挂买一/卖一外侧追逐）；limit_offset=偏移百分比限价（按市价±%挂单，超时且价格朝不利方向移动才重挂）"
          >
            <Select
              options={[
                { value: 'off', label: '关闭（普通市价）' },
                { value: 'maker_market', label: '市价转限价（maker_market）' },
                { value: 'limit_offset', label: '偏移百分比限价（limit_offset）' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="notify_email"
            label="通知邮箱"
            extra="下单/平仓后发送邮件提醒，不填则不发送"
          >
            <Input placeholder="例如: your@email.com" type="email" />
          </Form.Item>

          <Form.Item
            name="allow_trading"
            label="允许交易"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="allow_risk_check"
            label="允许风控检查"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="allow_short"
            label="允许开空"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="max_daily_signals"
            label="每日最大信号数"
            rules={[{ required: true, message: '请输入每日最大信号数' }]}
          >
            <InputNumber min={1} max={10000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="max_position_size_usdt"
            label="最大仓位金额(USDT)"
            rules={[{ required: true, message: '请输入最大仓位金额' }]}
          >
            <InputNumber min={1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="position_multiplier"
            label="仓位倍数"
            extra="Webhook 信号数量（开/平仓）× 此倍数，默认 1。例如 2=双倍，0.5=减半；平仓数量同样缩放（不超过实际持仓）。非交易所杠杆。"
            rules={[{ required: true, message: '请输入仓位倍数' }]}
          >
            <InputNumber min={0.01} max={1000} step={0.1} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="group_item_limits_json"
            label="Group 并发 item 限制"
            extra='Webhook 带 group+item 时生效。JSON 映射 group→n，例如 {"1":1,"2":2} 表示 group "1" 同时最多 1 个不同 item 持仓。未配置该 group 的信号会被拒绝。'
            initialValue="{}"
          >
            <Input.TextArea rows={4} placeholder='{"1": 1, "2": 2}' />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editingMutexRule ? '编辑互斥规则' : '新增互斥规则'}
        open={mutexModalVisible}
        onCancel={() => {
          setMutexModalVisible(false)
          setEditingMutexRule(null)
          mutexForm.resetFields()
        }}
        onOk={() => mutexForm.submit()}
        confirmLoading={createMutexMutation.isPending || updateMutexMutation.isPending}
      >
        <Form
          form={mutexForm}
          layout="vertical"
          onFinish={handleMutexSubmit}
          initialValues={{ enabled: true }}
        >
          <Form.Item
            name="token_a_id"
            label="Token A"
            rules={[{ required: true, message: '请选择Token A' }]}
          >
            <Select placeholder="请选择Token A" disabled={!!editingMutexRule}>
              {(tokens || []).map((t: TokenInfo) => (
                <Option key={t.id} value={t.id}>{t.name} (#{t.id})</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="token_b_id"
            label="Token B"
            rules={[{ required: true, message: '请选择Token B' }]}
          >
            <Select placeholder="请选择Token B" disabled={!!editingMutexRule}>
              {(tokens || []).map((t: TokenInfo) => (
                <Option key={t.id} value={t.id}>{t.name} (#{t.id})</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="symbols"
            label="互斥币种（逗号分隔）"
            rules={[{ required: true, message: '请输入至少一个symbol' }]}
            tooltip="示例：XRPUSDT,ADAUSDT"
          >
            <Input placeholder="XRPUSDT,ADAUSDT" />
          </Form.Item>
          <Form.Item name="enabled" label="启用规则" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情Drawer */}
      <Drawer
        title="Token详情"
        placement="right"
        width={600}
        open={detailDrawerVisible}
        onClose={() => setDetailDrawerVisible(false)}
      >
        {selectedToken && (
          <div>
            {/*
              TradingView webhook URL: front and API are served under same origin in this deployment.
              Use runtime origin so users can copy the correct URL for their environment/domain.
            */}
            {(() => {
              const webhookUrl = `${window.location.origin}/api/v1/webhook`
              const tvPayload = `{\n  \"token\": \"${selectedToken.token}\",\n  \"action\": \"{{strategy.order.action}}\",\n  \"ticker\": \"{{ticker}}\",\n  \"contracts\": \"{{strategy.order.contracts}}\",\n  \"price\": \"market\"\n}`
              return (
                <>
            <h3>基本信息</h3>
            <p><strong>ID:</strong> {selectedToken.id}</p>
            <p><strong>名称:</strong> {selectedToken.name}</p>
            <p><strong>描述:</strong> {selectedToken.description || '-'}</p>
            <p><strong>状态:</strong> {selectedToken.is_active ? '激活' : '禁用'}</p>
            <p><strong>创建时间:</strong> {new Date(selectedToken.created_at).toLocaleString()}</p>
            
            <Divider />
            
            <h3>Token密钥</h3>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Token值:</Text>
              <div style={{ 
                marginTop: 8, 
                padding: '8px 12px', 
                backgroundColor: '#f5f5f5', 
                border: '1px solid #d9d9d9',
                borderRadius: '6px',
                fontFamily: 'monospace',
                fontSize: '12px',
                wordBreak: 'break-all',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span style={{ flex: 1, marginRight: 8 }}>
                  {selectedToken.token}
                </span>
                <Button 
                  size="small" 
                  icon={<CopyOutlined />} 
                  onClick={() => copyToClipboard(selectedToken.token)}
                  title="复制Token"
                >
                  复制
                </Button>
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                ⚠️ 请妥善保管您的Token，不要泄露给他人
              </Text>
            </div>
            
            <Divider />

            <h3>TradingView 信号示例</h3>
            <div style={{ marginBottom: 16 }}>
              <Text strong>Webhook URL:</Text>
              <Paragraph
                style={{ marginTop: 8, marginBottom: 8 }}
                copyable={{ text: webhookUrl }}
              >
                <code style={{ fontSize: 12 }}>{webhookUrl}</code>
              </Paragraph>

              <Text strong>消息内容（JSON）:</Text>
              <div
                style={{
                  marginTop: 8,
                  padding: '8px 12px',
                  backgroundColor: '#f5f5f5',
                  border: '1px solid #d9d9d9',
                  borderRadius: '6px',
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                }}
              >
                {tvPayload}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button
                  size="small"
                  icon={<CopyOutlined />}
                  onClick={() => copyToClipboard(tvPayload)}
                >
                  复制消息内容
                </Button>
              </div>
              <Text type="secondary" style={{ fontSize: '12px' }}>
                contracts / ticker / action 使用 TradingView 占位符；price 使用 market（市价单）
              </Text>
            </div>
            
            <h3>交易所配置</h3>
            <p>
              <strong>绑定交易所:</strong>{' '}
              {selectedToken.exchange_name}
              {selectedToken.tradfi_enabled && <Tag color="orange" style={{ marginLeft: 8 }}>CFD</Tag>}
            </p>
            {selectedToken.tradfi_enabled && (
              <>
                <p>
                  <strong>保本止损:</strong>{' '}
                  {selectedToken.tradfi_breakeven_enabled ? (
                    <Tag color="green">已开启</Tag>
                  ) : (
                    <Tag>未开启</Tag>
                  )}
                </p>
                <p>
                  <strong>以损定仓:</strong>{' '}
                  {selectedToken.tradfi_risk_sizing_enabled ? (
                    <Tag color="green">已开启（{selectedToken.tradfi_risk_percent}%）</Tag>
                  ) : (
                    <Tag>未开启</Tag>
                  )}
                </p>
                <p>
                  <strong>手数补足 0.02:</strong>{' '}
                  {selectedToken.tradfi_pad_min_lots ? (
                    <Tag color="green">已开启</Tag>
                  ) : (
                    <Tag>未开启</Tag>
                  )}
                </p>
                <p>
                  <strong>黄金限价转市价:</strong>{' '}
                  {selectedToken.tradfi_gold_market_convert_enabled ? (
                    <Tag color="green">已开启（{selectedToken.tradfi_gold_market_convert_pct}%）</Tag>
                  ) : (
                    <Tag>未开启</Tag>
                  )}
                </p>
              </>
            )}
            <p>
              <strong>实例状态:</strong>{' '}
              <Tag color={selectedToken.exchange_status?.connected ? 'green' : 'red'}>
                {selectedToken.exchange_status?.connected ? '正常' : '异常'}
              </Tag>
            </p>
            {exchangeStatusLoading ? (
              <p>加载交易所状态...</p>
            ) : (
              <>
                <p><strong>总余额(USDT):</strong> {selectedExchangeStatus?.data?.account?.total_balance ?? '-'}</p>
                <p><strong>可用余额(USDT):</strong> {selectedExchangeStatus?.data?.account?.free_balance ?? '-'}</p>
                <p><strong>持仓数量:</strong> {selectedExchangeStatus?.data?.positions?.length ?? 0}</p>
              </>
            )}
            
            <h3>权限设置</h3>
            <p><strong>允许交易:</strong> {selectedToken.allow_trading ? '是' : '否'}</p>
            <p><strong>允许风控检查:</strong> {selectedToken.allow_risk_check ? '是' : '否'}</p>
            <p><strong>允许开空:</strong> {selectedToken.allow_short ? '是' : '否'}</p>
            <p><strong>执行模式:</strong> {
              (() => {
                const m = String(
                  selectedToken.execution_mode ||
                  (selectedToken.maker_market_enabled ? 'maker_market' : 'off')
                ).toLowerCase()
                const map: Record<string, string> = {
                  off: '关闭（普通市价）',
                  maker_market: '市价转限价',
                  limit_offset: '偏移百分比限价',
                }
                return map[m] || map.off
              })()
            }</p>
            <p><strong>通知邮箱:</strong> {selectedToken.notify_email || '未配置'}</p>
            <p><strong>每日最大信号数:</strong> {selectedToken.max_daily_signals}</p>
            <p><strong>最大仓位金额:</strong> {selectedToken.max_position_size_usdt} USDT</p>
            <p><strong>仓位倍数:</strong> {selectedToken.position_multiplier ?? 1}（Webhook 开/平仓数量乘数）</p>
            <p>
              <strong>Group 并发 item 限制:</strong>{' '}
              {selectedToken.group_item_limits && Object.keys(selectedToken.group_item_limits).length > 0
                ? JSON.stringify(selectedToken.group_item_limits)
                : '未配置'}
            </p>
            
            <h3>使用统计</h3>
            <p><strong>使用次数:</strong> {selectedToken.usage_count}</p>
            <p><strong>已处理信号:</strong> {selectedToken.total_signals_processed}</p>
            <p><strong>已创建订单:</strong> {selectedToken.total_orders_created}</p>
            <p><strong>总交易量:</strong> {selectedToken.total_trading_volume_usdt} USDT</p>
            <p><strong>最后使用:</strong> {selectedToken.last_used_at ? new Date(selectedToken.last_used_at).toLocaleString() : '-'}</p>
                </>
              )
            })()}
          </div>
        )}
      </Drawer>
    </div>
  )
}

export default Tokens

